import {CastingTimeUnit} from '../../../../generated/types';
import type {Attack} from './attacks';
import type {Catalog} from './catalog';
import type {Character} from './character';
import type {CharacterAction, CharacterSpell, SpellCasting} from './collect-effects';
import {canCastWithSlots} from './spell-casting';
import {rechargeLabel} from './labels';
import {isSpellReady, spellListEntries, spellPreparations, SpellListEntry} from './spell-list';
import type {ActionKind} from './types';

export type ActionSection = 'action' | 'bonusAction' | 'reaction' | 'other';

export const ACTION_SECTIONS: ActionSection[] = ['action', 'bonusAction', 'reaction', 'other'];

export type ActionEntry = {
	key: string;
	section: ActionSection;
	title: string;
	/** атака или урон: попадает в фильтр «Атаки» */
	isAttack: boolean;
	/** тратит использования, ресурс или ячейку заклинаний */
	isLimited: boolean;
	attack?: Attack;
	spellId?: string;
	/** счётчик использований в листе */
	useKey?: string;
	/** особенность, описание которой раскрывается в строке */
	featureKey?: string;
	/** статья глоссария с описанием */
	glossaryId?: string;
	note?: string;
	/** действие особенности: урон, испытание, восстановление ячеек */
	featureAction?: CharacterAction;
	/** заклинание накладывается как ритуал */
	isRitual?: boolean;
	/** заклинание и способ его наложения: ячейкой, использованиями или зарядами */
	spellCasting?: {spell: CharacterSpell; casting: SpellCasting};
};

export type StandardAction = {
	glossaryId: string;
	title: string;
};

export const STANDARD_ACTIONS: StandardAction[] = [
	{glossaryId: 'attack', title: 'Атака'},
	{glossaryId: 'magic', title: 'Магия'},
	{glossaryId: 'dash', title: 'Рывок'},
	{glossaryId: 'disengage', title: 'Отступление'},
	{glossaryId: 'dodge', title: 'Уклонение'},
	{glossaryId: 'help', title: 'Помощь'},
	{glossaryId: 'hide', title: 'Засада'},
	{glossaryId: 'influence', title: 'Влияние'},
	{glossaryId: 'ready', title: 'Подготовленное действие'},
	{glossaryId: 'search', title: 'Поиск'},
	{glossaryId: 'study', title: 'Изучение'},
	{glossaryId: 'utilize', title: 'Использование'},
].sort((first, second) => first.title.localeCompare(second.title));

const SECTION_BY_ACTION_KIND: Record<ActionKind, ActionSection> = {
	action: 'action',
	attack: 'action',
	magicAction: 'action',
	afterShortRest: 'other',
	bonusAction: 'bonusAction',
	reaction: 'reaction',
	free: 'other',
};

const ACTION_NOTES: Partial<Record<ActionKind | 'none', string>> = {
	magicAction: 'действие Магия',
	attack: 'вместо одной атаки действия Атака',
};

const SECTION_BY_CASTING_TIME: Partial<Record<CastingTimeUnit, ActionSection>> = {
	[CastingTimeUnit.Action]: 'action',
	[CastingTimeUnit.Bonus]: 'bonusAction',
	[CastingTimeUnit.Reaction]: 'reaction',
};

export function buildActionEntries(catalog: Catalog, character: Character): ActionEntry[] {
	return [
		...attackEntries(character),
		...opportunityAttackEntries(character),
		...featureEntries(catalog, character),
		...resourceEntries(catalog, character),
		...spellEntries(catalog, character).filter(
			(entry) =>
				entry.section === 'reaction' ||
				entry.section === 'bonusAction' ||
				catalog.getSpell(entry.spellId!)?.level === 0,
		),
	];
}

function attackEntries(character: Character): ActionEntry[] {
	const weaponEntries = character.weaponAttacks.map(
		(attack): ActionEntry => ({
			key: `attack:${attack.key}`,
			section: 'action',
			title: attack.title,
			isAttack: true,
			isLimited: false,
			attack,
		}),
	);
	const unarmed = character.unarmedStrike;
	const offHandEntries = character.offHandAttacks.map(
		(attack): ActionEntry => ({
			key: `attack:${attack.key}`,
			section: 'bonusAction',
			title: `${attack.title}: атака второй рукой`,
			isAttack: true,
			isLimited: false,
			attack,
		}),
	);
	return [
		...weaponEntries,
		{
			key: 'attack:unarmed-strike',
			section: 'action',
			title: unarmed.title,
			isAttack: true,
			isLimited: false,
			attack: unarmed,
			glossaryId: 'unarmed-strike',
		},
		...offHandEntries,
	];
}

function opportunityAttackEntries(character: Character): ActionEntry[] {
	return character.opportunityAttacks.map(
		(attack): ActionEntry => ({
			key: `opportunity:${attack.key}`,
			section: 'reaction',
			title: attack.title,
			isAttack: true,
			isLimited: false,
			attack,
			glossaryId: 'opportunity-attacks',
			note: 'Внеочередная атака',
		}),
	);
}

/** черта, которой принадлежит особенность: пишется в подписи под названием; совпадающее с названием не повторяется */
function featTitle(catalog: Catalog, character: Character, featureKey: string, title: string): string | undefined {
	const featId = character.getFeatureInstance(featureKey)?.feature.featId;
	const feat = featId ? catalog.getFeat(featId)?.title : undefined;
	return feat !== title ? feat : undefined;
}

const joinNote = (...parts: (string | undefined)[]) => parts.filter(Boolean).join(' · ') || undefined;

function featureEntries(catalog: Catalog, character: Character): ActionEntry[] {
	return character.actions
		.filter((action) => !isRestAction(action))
		.map((action): ActionEntry => {
			const useKey = actionUseKey(character, action);
			return {
				key: `feature:${action.key}`,
				section: action.kind ? SECTION_BY_ACTION_KIND[action.kind] : 'other',
				title: action.title,
				isAttack: !!action.damage || !!action.save,
				isLimited: !!useKey,
				/** у чужого ресурса (Острые слова тратят Бардовское вдохновение) метки показывает особенность-хозяин */
				useKey: isForeignResource(character, action) ? undefined : useKey,
				featureKey: action.featureKey,
				note: joinNote(
					featTitle(catalog, character, action.featureKey, action.title),
					ACTION_NOTES[action.kind || 'none'],
				),
				featureAction: action,
			};
		});
}

/** действия после Короткого отдыха показываются в окне отдыха, а не среди действий */
export const isRestAction = (action: CharacterAction) => action.kind === 'afterShortRest';

/** действие тратит ресурс другой особенности: Острые слова — кости Бардовского вдохновения */
export function foreignResource(character: Character, action: CharacterAction) {
	if (!action.resource) return undefined;
	const resource = character.resources.find(({id}) => id === action.resource);
	return resource && resource.featureKey !== action.featureKey ? resource : undefined;
}

const isForeignResource = (character: Character, action: CharacterAction) => !!foreignResource(character, action);

export function actionUseKey(character: Character, action: CharacterAction): string | undefined {
	const key = action.resource ? `resource:${action.resource}` : action.key;
	return character.limitedUse(key) ? key : undefined;
}

function resourceEntries(catalog: Catalog, character: Character): ActionEntry[] {
	/** ресурс со своим действием показывается в строке действия; остальные — отдельной строкой */
	const spentByOwnActions = new Set(
		character.actions
			.filter((action) => action.resource && !isForeignResource(character, action))
			.map((action) => action.resource),
	);
	return character.resources
		.filter((resource) => !spentByOwnActions.has(resource.id))
		.map((resource) => ({
			key: `resource:${resource.id}`,
			section: 'other',
			title: resource.title,
			isAttack: false,
			isLimited: true,
			useKey: resource.key,
			featureKey: resource.featureKey,
			note: joinNote(
				featTitle(catalog, character, resource.featureKey, resource.title),
				resource.die != null ? `кость ${resource.die}` : undefined,
			),
		}));
}

export function spellEntries(catalog: Catalog, character: Character): ActionEntry[] {
	const preparations = spellPreparations(catalog, character);
	const entries = spellListEntries(preparations, character);
	const spellbookRituals = entries.filter(
		(entry) =>
			!isSpellReady(entry) && isSpellbookEntry(entry, character) && catalog.getSpell(entry.spell.id)?.ritual,
	);

	return [
		...entries.filter(isSpellReady).flatMap((entry) => spellEntry(catalog, character, entry)),
		...spellbookRituals.map((entry) => ritualEntry(catalog, entry)),
	];
}

function spellEntry(catalog: Catalog, character: Character, {spell}: SpellListEntry): ActionEntry[] {
	const data = catalog.getSpell(spell.id);
	if (!data) return [];
	const castingSection = data.castingTime ? SECTION_BY_CASTING_TIME[data.castingTime.unit] : undefined;
	const hasOwnCasting = spell.castings.some((casting) => casting.kind !== 'slot');
	const castings = spell.castings.filter(
		(casting) =>
			casting.kind !== 'slot' || data.level === 0 || !hasOwnCasting || canCastWithSlots(character, data.level),
	);

	const classTitles = spell.classIds.map((classId) => catalog.getClass(classId)?.title || classId).join(', ');
	const entries = castings.map((casting): ActionEntry => {
		const useKey =
			casting.kind === 'resource' ? casting.resourceKey : casting.kind === 'uses' ? casting.key : undefined;
		return {
			key: `spell:${casting.key}`,
			section: castingSection || 'other',
			title: data.title,
			isAttack: !!data.attackType || data.damageTypes.length > 0,
			isLimited: data.level > 0 || casting.kind !== 'slot',
			spellId: spell.id,
			useKey: useKey && character.limitedUse(useKey) ? useKey : undefined,
			note: [spellNote(data.level), castingNote(character, casting, data.level), classTitles]
				.filter(Boolean)
				.join(' · '),
			spellCasting: {spell, casting},
		};
	});
	return data.ritual ? [...entries, ritualEntry(catalog, {spell})] : entries;
}

function castingNote(character: Character, casting: SpellCasting, level: number): string {
	if (casting.kind === 'slot') return level > 0 ? 'ячейкой' : '';
	const usage =
		casting.kind === 'resource'
			? 'заряды'
			: casting.recharge && !character.limitedUse(casting.key)
			? `без ячейки, восстановление: ${rechargeLabel(casting.recharge)}`
			: 'без ячейки';
	return [casting.source, usage].filter(Boolean).join(': ');
}

function ritualEntry(catalog: Catalog, {spell}: SpellListEntry): ActionEntry {
	const data = catalog.getSpell(spell.id);
	return {
		key: `ritual:${spell.id}`,
		section: 'other',
		title: data?.title || spell.id,
		isAttack: false,
		isLimited: false,
		spellId: spell.id,
		isRitual: true,
		note: 'ритуал, без траты ячейки',
	};
}

const spellNote = (level: number) => (level === 0 ? 'фокус' : `${level} круг`);

const isSpellbookEntry = ({preparation}: SpellListEntry, character: Character) =>
	!!preparation &&
	character.getFeatureInstance(preparation.choice.featureKey)?.feature.props?.spellcasting?.mode === 'spellbook';
